import { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button, Paper, Stack } from '@mui/material';
import { ErrorOutline as ErrorIcon } from '@mui/icons-material';

interface Props {
  children: ReactNode;
  /** Optional custom fallback UI. When provided, replaces the default error screen. */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // In production, swap this for a real logging service (e.g. Sentry)
    console.error('[ErrorBoundary] Uncaught error:', error, errorInfo.componentStack);
  }

  /** Attempt to recover without navigating away */
  private handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  /** Hard reload as last resort */
  private handleRefresh = (): void => {
    window.location.reload();
  };

  public render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
          p={3}
          // Announce error to screen readers without interrupting ongoing speech
          role="alert"
          aria-live="polite"
          aria-atomic="true"
        >
          <Paper
            elevation={3}
            sx={{ p: 4, maxWidth: 500, textAlign: 'center', borderRadius: 2 }}
          >
            <ErrorIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} aria-hidden="true" />
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Something went wrong
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              An unexpected error occurred. Please try again or refresh the
              page. Contact support if the problem persists.
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center" mt={2}>
              <Button variant="contained" onClick={this.handleRetry}>
                Try Again
              </Button>
              <Button variant="outlined" onClick={this.handleRefresh}>
                Refresh Page
              </Button>
            </Stack>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
